import { roundToPrecision } from '../../../../libs/frameUtils';
import type { TextAlignment } from '../../../../enums';
import type { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyleRenderer';
import type { CanvasTextMetrics } from './CanvasTextMetrics';
import { ROUNDING_PRECISION, buildMetricsForText } from './CanvasTextMetrics';

const RANGE_TOLERANCE: number = 0.1;

type CanvasFontSizeToFitAllTextBinarySearchSettings = {
    text: string;
    alignment?: TextAlignment;
    normalStyle: CanvasTextStyleRenderer;
    highlightStyle: CanvasTextStyleRenderer;
    highlightAreas?: Array<[number, number]>;
    wordBreak?: boolean;
    maxWidth?: number;
    maxHeight?: number;
};

/**
 * Binary search to find the font size that fits all text within the given bounds.
 */
export class CanvasFontSizeToFitAllTextBinarySearch {
    #text: string;
    #alignment: TextAlignment;
    #normalStyle: CanvasTextStyleRenderer;
    #highlightStyle: CanvasTextStyleRenderer;
    #highlightAreas: Array<[number, number]>;
    #wordBreak: boolean;
    #maxWidth: number;
    #maxHeight: number;
    #metrics: CanvasTextMetrics = null;
    #fontSizeCache = new Map();

    private constructor(settings: CanvasFontSizeToFitAllTextBinarySearchSettings) {
        this.#text = settings.text;
        this.#alignment = settings.alignment;
        this.#normalStyle = settings.normalStyle;
        this.#highlightStyle = settings.highlightStyle;
        this.#highlightAreas = settings.highlightAreas;
        this.#wordBreak = settings.wordBreak;
        this.#maxWidth = settings.maxWidth;
        this.#maxHeight = settings.maxHeight;
    }

    static search({
        text,
        alignment,
        normalStyle,
        highlightStyle,
        highlightAreas,
        wordBreak,
        maxWidth,
        maxHeight,
        fontSizeRange,
    }: {
        text: string;
        alignment: TextAlignment;
        normalStyle: CanvasTextStyleRenderer;
        highlightStyle: CanvasTextStyleRenderer;
        highlightAreas: Array<[number, number]>;
        wordBreak: boolean;
        maxWidth: number;
        maxHeight: number;
        fontSizeRange: { min: number; max: number };
    }): {
        displaysAllText: boolean;
        fontSize: number;
    } {
        const search = new CanvasFontSizeToFitAllTextBinarySearch({
            text,
            alignment,
            normalStyle: normalStyle.clone(),
            highlightStyle: highlightStyle.clone(),
            highlightAreas,
            wordBreak,
            maxWidth,
            maxHeight,
        });

        const minValue = fontSizeRange.min;
        const maxValue = Math.min(fontSizeRange.max, maxHeight);
        const fontSize =
            maxValue < minValue ? minValue : search.#recursiveSearch(minValue, maxValue);

        return {
            fontSize,
            displaysAllText: search.#canDisplayAllText(fontSize),
        };
    }

    #recursiveSearch(minValue: number, maxValue: number) {
        const halfRange = roundToPrecision((maxValue - minValue) / 2, ROUNDING_PRECISION);
        if (halfRange <= RANGE_TOLERANCE) {
            // at this point we have 2 values left, we need to pick one
            return this.#canDisplayAllText(maxValue) ? maxValue : minValue;
        }
        const nextValue = maxValue - halfRange;
        if (!this.#canDisplayAllText(nextValue)) {
            return this.#recursiveSearch(minValue, minValue + halfRange);
        } else {
            return this.#recursiveSearch(maxValue - halfRange, maxValue);
        }
    }

    #canDisplayAllText(fontSize: number) {
        if (this.#fontSizeCache.has(fontSize)) {
            return this.#fontSizeCache.get(fontSize);
        }
        this.#normalStyle.fontSize = fontSize;
        this.#highlightStyle.fontSize = fontSize;
        this.#metrics = buildMetricsForText({
            text: this.#text,
            alignment: this.#alignment,
            normalStyle: this.#normalStyle,
            highlightStyle: this.#highlightStyle,
            highlightAreas: this.#highlightAreas,
            wordBreak: this.#wordBreak,
            maxWidth: this.#maxWidth,
            maxHeight: this.#maxHeight,
        });
        const canDisplayText =
            this.#metrics.canDisplayAllText && this.#normalStyle.baselineOffset <= this.#maxHeight;
        this.#fontSizeCache.set(fontSize, canDisplayText);
        return canDisplayText;
    }
}
